/**
 * Enum labels — `/v1/admin/enum-labels` (schema §11, doc 05-admin/03 §10).
 *
 * The words every chip, picker and booking-form field shows a code as. The server lints each
 * write in the row's own language and audits it; this file only moves the request.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type EnumLabelRow = components['schemas']['EnumLabelRow'];
export type EnumLabelList = components['schemas']['EnumLabelListResponse'];
export type EnumLabelIn = components['schemas']['EnumLabelIn'];
export type EnumLabelPatch = components['schemas']['EnumLabelPatchIn'];

/** `name` empty reads only the vocabulary list — the server returns every row otherwise. */
export function useEnumLabels(name: string) {
  return useQuery({
    queryKey: keys.enumLabels.of(name),
    queryFn: () =>
      authedClient.request<EnumLabelList>(
        name ? `/v1/admin/enum-labels?enum_name=${encodeURIComponent(name)}` : '/v1/admin/enum-labels',
      ),
  });
}

export type ConsoleLabel = components['schemas']['ConsoleLabel'];

/**
 * The words every console chip shows a state as — `/v1/admin/enum-labels/resolved` (R314).
 *
 * Readable by every admin scope, unlike the list above, and already resolved server-side: the
 * `admin` row where one exists, else `all`, active rows only. Read once for all vocabularies and
 * shared by every chip on the page.
 */
export function useConsoleLabels() {
  return useQuery({
    queryKey: keys.enumLabels.resolved(),
    queryFn: () =>
      authedClient.request<components['schemas']['ConsoleLabelsResponse']>('/v1/admin/enum-labels/resolved'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateEnumLabel() {
  const client = useQueryClient();
  return useMutation<EnumLabelRow, unknown, EnumLabelIn>({
    mutationFn: (body) =>
      authedClient.request<EnumLabelRow>('/v1/admin/enum-labels', { method: 'POST', body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.enumLabels.all() });
    },
  });
}

export function useUpdateEnumLabel() {
  const client = useQueryClient();
  return useMutation<EnumLabelRow, unknown, { id: number; patch: EnumLabelPatch }>({
    mutationFn: ({ id, patch }) =>
      authedClient.request<EnumLabelRow>(`/v1/admin/enum-labels/${id}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.enumLabels.all() });
    },
  });
}
